import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, LogOut, Users, ArrowUpRight, Table2, Building2, BookOpen, History, FileSpreadsheet, BarChart3, Database, ShieldAlert, NotebookPen, Replace } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { APP_VERSION } from '@uniticket/shared';

// Reduced from uniticket Sidebar: only the internal "Validation → Address validation" entry, plus
// the user footer + sign-out. (Dashboard/tickets/admin/external-operator-logo sections are out of
// scope.) The Avatar primitive is inlined as initials to avoid an extra dependency.

interface SidebarProps {
  onNavigate?: () => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const { user, isInternal, isAdmin, isBuild, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  async function handleSignOut() {
    await logout();
    navigate('/login');
    onNavigate?.();
  }

  return (
    <aside className="flex flex-col w-64 h-full bg-white border-r border-slate-200">
      {/* Logo */}
      <div className="flex items-center gap-3 h-16 px-5 border-b border-slate-100">
        {/* La marque de l'app. `alt` vide à dessein : le nom est juste à côté en texte, un alt le
            ferait annoncer deux fois par un lecteur d'écran. Le carré à icône dégradé reste le motif
            d'en-tête DE CHAQUE PAGE (chacune la sienne) — ce n'est pas la marque, on n'y touche pas. */}
        <img src="/logo-av.png" alt="" className="w-8 h-8 rounded-lg" />
        <span className="text-base font-bold gradient-text">Address Validation</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto custom-scrollbar py-4">
        {isInternal && (
          <>
            <div className="px-5 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t('nav.validation')}
              </span>
            </div>
            <div className="space-y-0.5 px-2">
              <NavItem to="/address-validations" label={t('nav.addressValidation')} icon={ClipboardCheck} onNavigate={onNavigate} />
              <NavItem to="/mdu-requests" label={t('nav.mduRequests')} icon={Building2} onNavigate={onNavigate} />
              {/* Accès direct à la console, sans passer par un ticket (demande user 2026-08-07).
                  Admins seulement pour l'instant — le tiroir reste le chemin des validateurs. */}
              {isAdmin && <NavItem to="/dmp-console" label={t('nav.dmpConsole')} icon={Database} onNavigate={onNavigate} />}
              {/* Renumbering (H-1) : admins seulement, mais dans le bloc MÉTIER — c'est du traitement
                  de lot, pas un réglage. Même gabarit que la console juste au-dessus. */}
              {isAdmin && <NavItem to="/renumbering" label={t('nav.renumbering')} icon={Replace} onNavigate={onNavigate} />}
              {/* Notes perso (demande user 2026-09-10) : réponses types partagées + bloc-notes privé.
                  Dans le bloc INTERNE, donc invisible du compte Build — voulu. */}
              <NavItem to="/notes" label={t('nav.notes')} icon={NotebookPen} onNavigate={onNavigate} />
              <NavItem to="/documentation" label={t('nav.documentation')} icon={BookOpen} onNavigate={onNavigate} />
            </div>
          </>
        )}

        {/* Compte Build (user 2026-08-07) : la console DMP est sa seule entrée — pas de file, pas
            d'administration. Les internes gardent la console dans le tiroir, comme avant. */}
        {isBuild && (
          <>
            <div className="px-5 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t('nav.consultation')}
              </span>
            </div>
            <div className="space-y-0.5 px-2">
              <NavItem to="/dmp-console" label={t('nav.dmpConsole')} icon={Database} onNavigate={onNavigate} />
            </div>
          </>
        )}

        {isAdmin && (
          <>
            <div className="px-5 mt-6 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t('nav.administration')}
              </span>
            </div>
            <div className="space-y-0.5 px-2">
              <NavItem to="/admin/validation-stats" label={t('nav.validationStats')} icon={BarChart3} onNavigate={onNavigate} />
              <NavItem to="/admin/users" label={t('nav.userManagement')} icon={Users} onNavigate={onNavigate} />
              <NavItem to="/admin/mapping-table" label={t('nav.mappingTable')} icon={Table2} onNavigate={onNavigate} />
              <NavItem to="/admin/handoff-settings" label={t('nav.handoffSettings')} icon={ArrowUpRight} onNavigate={onNavigate} />
              <NavItem to="/admin/action-log" label={t('nav.actionLog')} icon={History} onNavigate={onNavigate} />
              <NavItem to="/admin/jvid-registry" label={t('nav.jvidRegistry')} icon={FileSpreadsheet} onNavigate={onNavigate} />
              <NavItem to="/admin/blacklist-feedback" label={t('nav.blacklistFeedback')} icon={ShieldAlert} onNavigate={onNavigate} />
            </div>
          </>
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center gap-3 w-full">
          <div className="h-9 w-9 shrink-0 rounded-full bg-brand-gradient text-white flex items-center justify-center text-[11px] font-bold">
            {user ? getInitials(user.displayName) : '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.displayName ?? ''}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email ?? ''}</p>
          </div>
        </div>
        {/* Clear, labeled sign-out button (replaces the easy-to-miss icon that sat next to the name). */}
        <button
          type="button"
          onClick={handleSignOut}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-brand-rose/40 hover:bg-brand-rose/[0.04] hover:text-brand-rose"
        >
          <LogOut className="w-4 h-4" />
          {t('topbar.signOut')}
        </button>
        <p className="mt-2 text-[10px] text-slate-300 text-center select-none">v{APP_VERSION}</p>
      </div>
    </aside>
  );
}

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onNavigate?: () => void;
}

function NavItem({ to, label, icon: Icon, onNavigate }: NavItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
          isActive
            ? 'text-brand-rose bg-gradient-to-r from-brand-rose/[0.08] to-transparent border-r-[3px] border-brand-rose'
            : 'text-slate-600 hover:bg-black/[0.03]'
        )
      }
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </NavLink>
  );
}
